import { Component, OnInit, ViewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { UserFormPanelComponent } from './user-form-panel/user-form-panel.component';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { SessionMgmtService } from 'src/app/shared/services/session-mgmt/session-mgmt.service';
import * as _ from 'lodash';

@Component({
    selector: 'app-account-settings-user-access',
    templateUrl: './account-settings-user-access.component.html',
    styleUrls: ['./account-settings-user-access.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, UserFormPanelComponent]
})
export class AccountSettingsUserAccessComponent implements OnInit {
  @ViewChild(UserFormPanelComponent) sidePanelComponent!: UserFormPanelComponent;

  accountId!: string;
  accountName: string = '';
  loggedInManagerId: string = '';
  managers: any[] = [];
  tempManagers: any[] = [];
  isPageBusy: boolean = false;
  isNoAdminUser: boolean = false;
  isNoMoreData: boolean = false;
  page_number: number = 1;
  limit: number = 12;
  callApi: boolean = true;
  brands_name: { [key: string]: string } = {};
  revokeDetails: any = {};
  showRevokeModal: boolean = false;
  isNoAccountAccess: boolean = false;
  isUserHasWriteAccess: boolean = false;

  constructor(
    private AccountsService: AccountsService,
    private route: ActivatedRoute,
    private _toast: ToastService,
    private appStateService: AppStateService,
    private sessionMgmt: SessionMgmtService
  ) {
    effect(() => {
      if (this.appStateService.initialized()) {
        this.isUserHasWriteAccess = this.isUserHasWriteAccessFn();
      }
    });
  }

  ngOnInit(): void {
    this.isUserHasWriteAccess = this.isUserHasWriteAccessFn();
    this.route.queryParams.subscribe(params => {
      this.accountId = params['account_id'] || localStorage.getItem('LtMsAcTiDfR');
      this.AccountsService.getAccountDetail(this.accountId).subscribe((response) => {
        this.accountName = response.data.account.display_name || response.data.account.name;
      });
      this.getAdminManagers();
    });
  }

  isUserHasWriteAccessFn(): boolean {
    const adminRulesDic = _.cloneDeep(window['Helpers']?.adminRulesDic || {});
    if (adminRulesDic.ACCOUNT_CREATE_NEW_USER && !_.isEmpty(adminRulesDic.ACCOUNT_CREATE_NEW_USER) && adminRulesDic.ACCOUNT_CREATE_NEW_USER.write) {
      return true;
    }
    return false;
  }

  /**
   * Fetch all admin managers for the selected account
   */
  getAdminManagers(): void {
    this.isPageBusy = true;
    
    if (!this.callApi) {
      this.prepareManagersList();
      this.isPageBusy = false;
      return;
    }

    if (this.accountId && this.callApi) {
      this.AccountsService.getAccountManagersByAccountId(this.accountId, this.limit, this.page_number).subscribe({
        next: (response) => {
          if (response.code === 0 && response.data && response.data.managers && response.data.managers.length) {
            // Store brand details for display
            if (response.data.brand_details && response.data.brand_details.length) {
              const project_details = response.data.brand_details;
              for (let i = 0; i < project_details.length; i++) {
                this.brands_name[project_details[i]._id] = project_details[i].name;
              }
            }

            this.tempManagers = this.tempManagers.concat(response.data.managers);
            
            if (response.data.managers.length === this.limit) {
              this.callApi = true;
            } else {
              this.callApi = false;
            }
            
            this.prepareManagersList();
          } else if (response && response.code === 4054) {
            this.callApi = false;
            this.prepareManagersList();
          } else {
            this._toast.showPopup('ERROR', 'Error', 'An internal server error has occurred. Please try again later.', 5000);
          }
          this.isPageBusy = false;
        },
        error: () => {
          this.isNoAccountAccess = true;
          this.isPageBusy = false;
          this._toast.showPopup('ERROR', 'Error', 'Failed to load admin managers.', 5000);
        }
      });
    } else {
      this.isPageBusy = false;
    }
  }

  /**
   * Prepare the managers list for display (paginated)
   */
  prepareManagersList(): void {
    this.loggedInManagerId = this.getLoggedInManager()?._id;

    // Include the logged-in manager in the admin list with disable delete btn
    // if (loggedInManagerId) {
    //   // Exclude the logged-in manager from the list
    //   this.tempManagers = this.tempManagers.filter(m => m._id !== loggedInManagerId);
    // }

    // Paginate managers list
    const startIdx = (this.page_number - 1) * 10;
    const endIdx = this.page_number * 10;
    
    for (let idx = startIdx; idx < endIdx; idx++) {
      if (this.tempManagers[idx]) {
        this.managers.push(this.tempManagers[idx]);
      }
    }

    this.isNoAdminUser = this.managers.length === 0;

    if (this.tempManagers.length === this.managers.length) {
      this.isNoMoreData = true;
    } else {
      this.isNoMoreData = false;
    }

    this.page_number += 1;
  }

  /**
   * Open the sidebar to add a new admin
   */
  openAddAdminPanel(): void {
    if (!this.isUserHasWriteAccess) {
      this._toast.showPopup('ERROR', 'Access Denied', 'You do not have write permission to add admins. Only read access is available.', 4000);
      return;
    }
    if (this.sidePanelComponent) {
      this.sidePanelComponent.open();
    }
  }

  /**
   * Handle admin created event
   */
  onAdminCreated(): void {
    // Reset and reload the managers list
    this.page_number = 1;
    this.managers = [];
    this.tempManagers = [];
    this.callApi = true;
    this.getAdminManagers();
  }

  /**
   * Prepare to revoke access
   */
  prepareToRemoveAccess(email_address: string, admin_id: string, type: string, name: string, id: string): void {
    if (!this.isUserHasWriteAccess) {
      this._toast.showPopup('ERROR', 'Access Denied', 'You do not have write permission to revoke access. Only read access is available.', 4000);
      return;
    }

    this.revokeDetails = {
      admin_manager_id: admin_id,
      email_address: email_address,
      type: type
    };

    if (type === 'account') {
      this.revokeDetails.account_id = id;
      this.revokeDetails.account_name = name;
    }
    if (type === 'brand') {
      this.revokeDetails.brand_id = id;
      this.revokeDetails.brand_name = name;
    }

    this.showRevokeModal = true;
  }

  /**
   * Revoke manager access
   */
  removeAccess(): void {
    if (!this.isUserHasWriteAccess) {
      this._toast.showPopup('ERROR', 'Access Denied', 'You do not have write permission to revoke access. Only read access is available.', 4000);
      this.showRevokeModal = false;
      return;
    }

    this.AccountsService.revokeManagerAccess(this.revokeDetails).subscribe({
      next: (response) => {
        if (response && response.code === 0) {
          // Reset and reload the managers list
          this.page_number = 1;
          this.managers = [];
          this.tempManagers = [];
          this.callApi = true;
          this.getAdminManagers();

          if (this.revokeDetails.type === 'account') {
            this._toast.showPopup('SUCCESS', 'Success', 'User access revoked successfully for the Account and associated Projects.', 3000);
          } else {
            this._toast.showPopup('SUCCESS', 'Success', 'Access has been removed successfully.', 3000);
          }
        } else {
          this._toast.showPopup('ERROR', 'Error', response.error_message || 'Failed to revoke access', 3000);
        }
        this.showRevokeModal = false;
      },
      error: (error) => {
        console.error('Error revoking access:', error);
        this._toast.showPopup('ERROR', 'Error', 'An error occurred while revoking access. Please try again.', 3000);
        this.showRevokeModal = false;
      }
    });
  }

  /**
   * Close revoke modal
   */
  closeRevokeModal(): void {
    this.showRevokeModal = false;
    this.revokeDetails = {};
  }

  /**
   * Toggle manager access list expansion
   */
  toggleManagerExpansion(manager: any): void {
    manager.isCollapsed = !manager.isCollapsed;
  }

  /**
   * TrackBy function for manager list
   */
  trackByManagerId(index: number, manager: any): string {
    return manager._id || index;
  }

  getCreatorName(manager: any): string {
    const access = manager?.manager_access?.[0];
    const createdBy = access?.created_by;
    if (!createdBy) {
      return 'NA';
    }

    if (typeof createdBy === 'object') {
      return createdBy.name || createdBy.user_name || createdBy.email_address || 'NA';
    }

    const matchedManager = this.findManagerById(createdBy);
    return matchedManager?.name || matchedManager?.email_address || 'NA';
  }

  getCreatedDate(manager: any): string {
    const createdDateValue = manager?.manager_access?.[0]?.created_date;
    if (!createdDateValue) {
      return 'NA';
    }
    const createdDate = new Date(createdDateValue);
    if (Number.isNaN(createdDate.getTime())) {
      return 'NA';
    }
    return createdDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  private getLoggedInManager(): any {
    const helpersManager = window['Helpers']?.managerDetails?.manager;
    if (helpersManager?._id) {
      return helpersManager;
    }

    const session = this.sessionMgmt.getData('LOGIN_RESPONSE');
    if (!session) {
      return null;
    }

    if (typeof session === 'string') {
      try {
        return JSON.parse(session)?.manager || null;
      } catch {
        return null;
      }
    }

    return session?.manager || null;
  }

  private findManagerById(managerId: string): any {
    const loadedManager = [...this.tempManagers, ...this.managers].find((item: any) => item?._id === managerId);
    if (loadedManager) {
      return loadedManager;
    }

    const sessionManager = this.getLoggedInManager();
    if (sessionManager?._id === managerId) {
      return sessionManager;
    }

    return null;
  }
}
